import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import AppStoreLink from '../components/common/AppStoreLink';
import TripMap from '../components/booking/TripMap';
import { usePublicDeclaredLoadTracking } from '../hooks/usePublicDeclaredLoadTracking';
import { formatEta, formatScheduledAt, statusToHuman } from '../utils/tripFormatting';
import type { PublicDeclaredLoadTracking, PublicTrackingAddress } from '../types/publicTracking';

function position(address?: PublicTrackingAddress | null) {
  if (address?.latitude == null || address.longitude == null) return null;
  return { lat: address.latitude, lng: address.longitude };
}

function formatDistance(km?: number | null): string | null {
  if (km == null || !Number.isFinite(km) || km <= 0) return null;
  return km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`;
}

function formatTime(iso?: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
}

function trackingCopy(data: PublicDeclaredLoadTracking | null): { title: string; detail: string } {
  const tracking = data?.tracking;

  if (!data) {
    return { title: 'Loading tracking', detail: 'Fetching the latest delivery position.' };
  }

  if (tracking?.driverFinishingAnotherDelivery) {
    return {
      title: 'Driver finishing another delivery',
      detail: tracking.trackingMessage || 'Live movement starts when the driver heads to pickup.',
    };
  }

  if (tracking?.locationTrusted === false) {
    return {
      title: 'Location being verified',
      detail: 'The map will update as soon as the next trusted driver location arrives.',
    };
  }

  if (tracking?.driverLatitude == null || tracking.driverLongitude == null) {
    return {
      title: statusToHuman(data.status),
      detail:
        tracking?.trackingMessage ||
        (data.driver?.name
          ? 'Live tracking starts when your driver shares their trip location.'
          : 'We will show live driver movement here as soon as dispatch is confirmed.'),
    };
  }

  return {
    title: tracking.displayLabel || statusToHuman(data.status),
    detail:
      tracking.trackingMessage ||
      (tracking.trackingMode === 'LOW_DATA'
        ? 'Tracking is updating in low-data mode.'
        : 'Tracking is refreshing automatically.'),
  };
}

function etaFor(data: PublicDeclaredLoadTracking | null): string | null {
  const tracking = data?.tracking;
  if (!tracking) return null;
  return formatEta(tracking.etaToPickupSec ?? tracking.etaToDropoffSec);
}

const PublicTrackingPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { data, loading, refreshing, connected, missing, expired, error, lastLoadedAt, refresh } =
    usePublicDeclaredLoadTracking(token);

  const pickup = useMemo(() => position(data?.pickup), [data?.pickup]);
  const dropoff = useMemo(() => position(data?.destination), [data?.destination]);
  const driver = useMemo(() => {
    const lat = data?.tracking?.driverLatitude;
    const lng = data?.tracking?.driverLongitude;
    return lat != null && lng != null ? { lat, lng } : null;
  }, [data?.tracking?.driverLatitude, data?.tracking?.driverLongitude]);

  const copy = trackingCopy(data);
  const eta = etaFor(data);
  const distance = formatDistance(data?.tracking?.distanceKm);
  const scheduled = data?.scheduledAt ? formatScheduledAt(data.scheduledAt) : null;
  const lastSeen = formatTime(data?.tracking?.lastSeenAt ?? data?.tracking?.updatedAt ?? data?.tracking?.locationAt);
  const assignedDriver = data?.driver && (data.driver.name || data.driver.avatarUrl || data.driver.rating) ? data.driver : null;

  return (
    <div className="landing-shell sw-public-track-shell">
      <Header />
      <main>
        <section className="sw-section sw-public-track-section">
          <div className="container">
            <div className="sw-public-track-head">
              <div>
                <span className="sw-eyebrow">Live delivery</span>
                <h1>{data ? copy.title : 'Track your delivery'}</h1>
                <p>{copy.detail}</p>
                {data ? (
                  <p className="sw-trip-note">
                    This link works in your browser, so you can follow the booking from WhatsApp or email without installing the app.
                  </p>
                ) : null}
              </div>
              {data?.bookingReference ? (
                <span className="sw-trip-code">{data.bookingReference}</span>
              ) : null}
            </div>

            {loading ? <p className="sw-trip-note">Loading your delivery tracking...</p> : null}

            {!loading && (missing || expired || error) && !data ? (
              <div className="sw-trip-panel">
                <div className="sw-trip-panel-head">
                  <h2 className="sw-choice-title">
                    {expired ? 'Tracking link expired' : missing ? 'Tracking link not found' : 'Tracking unavailable'}
                  </h2>
                </div>
                <p className="sw-trip-note">{error || 'Please ask your driver for a fresh tracking link.'}</p>
                <div className="sw-hero-actions sw-trip-actions">
                  <button className="sw-btn-secondary" onClick={() => void refresh()} type="button">
                    Try again
                  </button>
                  <Link className="sw-link-action" to="/about#contact">
                    Contact support
                  </Link>
                </div>
              </div>
            ) : null}

            {data ? (
              <>
                <div className="sw-public-track-map-wrap">
                  <TripMap
                    driver={driver}
                    driverHeading={data.tracking?.headingDeg}
                    dropoff={dropoff}
                    label={`Map of delivery ${data.bookingReference}`}
                    pickup={pickup}
                    routePolyline={data.tracking?.routePolyline}
                  />
                  <aside className="sw-public-track-overlay" aria-label="Delivery status">
                    <span className="sw-public-track-live">
                      {connected ? 'Live tracking' : refreshing ? 'Refreshing' : 'Auto refresh'}
                    </span>
                    <h2>{copy.title}</h2>
                    <p>{copy.detail}</p>
                    <dl>
                      <div>
                        <dt>Status</dt>
                        <dd>{statusToHuman(data.status)}</dd>
                      </div>
                      {eta ? (
                        <div>
                          <dt>ETA</dt>
                          <dd>{eta}</dd>
                        </div>
                      ) : null}
                      {lastSeen ? (
                        <div>
                          <dt>Updated</dt>
                          <dd>{lastSeen}</dd>
                        </div>
                      ) : null}
                    </dl>
                  </aside>
                </div>

                <dl className="sw-trip-facts sw-public-track-facts">
                  <div>
                    <dt>Pickup</dt>
                    <dd>{data.pickup?.address || 'Pickup address pending'}</dd>
                  </div>
                  <div>
                    <dt>Destination</dt>
                    <dd>{data.destination?.address || 'Destination address pending'}</dd>
                  </div>
                  {scheduled ? (
                    <div>
                      <dt>Scheduled</dt>
                      <dd>{scheduled}</dd>
                    </div>
                  ) : null}
                  {distance ? (
                    <div>
                      <dt>Distance</dt>
                      <dd>{distance}</dd>
                    </div>
                  ) : null}
                </dl>

                <div className="sw-public-track-grid">
                  <section className="sw-trip-panel">
                    <div className="sw-public-track-driver">
                      {assignedDriver?.avatarUrl ? (
                        <img alt="" src={assignedDriver.avatarUrl} />
                      ) : assignedDriver?.name ? (
                        <span aria-hidden="true">{assignedDriver.name.slice(0, 1).toUpperCase()}</span>
                      ) : (
                        <span aria-hidden="true">SW</span>
                      )}
                      <div>
                        <h2 className="sw-choice-title">{assignedDriver?.name || 'Driver not assigned yet'}</h2>
                        <p className="sw-trip-note">
                          {assignedDriver?.rating
                            ? `${assignedDriver.rating.toFixed(1)} rating`
                            : assignedDriver?.name
                              ? 'Verified ShiftWagon driver'
                              : 'Dispatch is still finding a verified driver for this booking.'}
                        </p>
                      </div>
                    </div>
                    <dl className="sw-trip-facts">
                      <div>
                        <dt>Vehicle</dt>
                        <dd>
                          {assignedDriver
                            ? data.vehicle?.displayName || data.vehicle?.type || 'Vehicle assigned'
                            : data.vehicle?.displayName || data.vehicle?.type || 'Pending assignment'}
                        </dd>
                      </div>
                    </dl>
                  </section>

                  <section className="sw-trip-panel">
                    <div className="sw-trip-panel-head">
                      <h2 className="sw-choice-title">Delivery progress</h2>
                    </div>
                    <ol className="sw-public-track-timeline">
                      {(data.timeline || []).map((item) => (
                        <li className={item.completed ? 'is-complete' : ''} key={item.key}>
                          <span aria-hidden="true" />
                          <div>
                            <strong>{item.label}</strong>
                            {item.at ? <small>{formatScheduledAt(item.at)}</small> : null}
                          </div>
                        </li>
                      ))}
                    </ol>
                  </section>
                </div>

                <div className="sw-hero-actions sw-trip-actions">
                  <button className="sw-btn-secondary" onClick={() => void refresh()} type="button">
                    Refresh
                  </button>
                  <AppStoreLink className="sw-link-action" placement="public_declared_load_tracking">
                    Track in the app
                  </AppStoreLink>
                </div>

                {lastLoadedAt ? (
                  <p className="sw-trip-note">Last checked at {formatTime(lastLoadedAt.toISOString())}.</p>
                ) : null}
              </>
            ) : null}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PublicTrackingPage;
